import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InputNumber, LoadingState, Select } from "components";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { useReturnOrder } from "hooks";
import { useSaleOrder } from "hooks";
import {
  ReturnOrder,
  ReturnOrderForm,
  ReturnOrderItem,
  WmsCode,
  WmsItem,
  WmsItemGroup,
} from "types";

type ReturnOrderItemWithMeta = ReturnOrderItem & { id?: number | string; __tempId?: string };

import { ROUTES } from "utils";
import SnackbarUtils from "utils/SnackbarUtils";
import { debounce } from "utils/helpers/data_helpers";
import { wmsCodeService, wmsItemGroupService, wmsItemService } from "services";
import {
  parseNumberSafe as normalizeNumber,
  normalizeDateInput,
} from "utils/helpers/accounting";
import { sanitizeReturnOrderItems } from "utils/helpers/sanitizers";
import { accountCodeService } from "services/accountCodeService";

const todayString = () => new Date().toISOString().slice(0, 10);
const ATTACHMENT_SPLIT_REGEX = /[,\n]/;

const defaultFormValues: ReturnOrderForm = {
  return_date: todayString(),
  due_date: todayString(),
  customer_id: undefined,
  customer_name: "",
  receiver_id: undefined,
  receiver_name: "",
  sale_order_id: undefined,
  sale_order_code: "",
  payment_method: "",
  refund_now: true,
  no_refund: false,
  description: "",
  attachments: [],
  items: [],
};

const normalizeAttachments = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item ?? "")))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item : String(item ?? "")))
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch {
      // ignore invalid JSON
    }

    return trimmed
      .split(ATTACHMENT_SPLIT_REGEX)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeId = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const sanitizeItems = (items?: ReturnOrderItem[]): ReturnOrderItem[] =>
  sanitizeReturnOrderItems(items);

const ReturnOrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { fetchDetail, createReturnOrder, updateReturnOrder } =
    useReturnOrder();
  const { fetchList: fetchSaleOrders } = useSaleOrder();

  const { data: detailResponse, isFetching: isFetchingDetail } = fetchDetail(
    id ?? "",
    isEdit,
  );
  const { data: saleOrdersData } = fetchSaleOrders(
    { page: 1, pageSize: 200 },
    true,
  );

  const [form, setForm] = useState<ReturnOrderForm>(defaultFormValues);
  const [attachmentsInput, setAttachmentsInput] = useState<string[]>([]);
  const [attachmentsText, setAttachmentsText] = useState<string>("");
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const [items, setItems] = useState<ReturnOrderItemWithMeta[]>([]);
  const [wmsCodes, setWmsCodes] = useState<WmsCode[]>([]);
  const [itemGroups, setItemGroups] = useState<WmsItemGroup[]>([]);
  const [wmsItems, setWmsItems] = useState<WmsItem[]>([]);
  const itemIdRef = useRef(0);
  const [units, setUnits] = useState<any[]>([]);

  const ensureTempIds = useCallback(
    (list: ReturnOrderItemWithMeta[]): ReturnOrderItemWithMeta[] =>
      list.map((item) =>
        item.__tempId
          ? item
          : { ...item, __tempId: `temp-${itemIdRef.current++}` },
      ),
    [],
  );

  useEffect(() => {
    if (!detailResponse?.data) return;
    const order = detailResponse.data as ReturnOrder;
    const normalizedAttachments = normalizeAttachments(order.attachments);
    const sanitizedItems = sanitizeItems(order.items);

    setForm({
      return_date: normalizeDateInput(order.return_date) || todayString(),
      due_date:
        normalizeDateInput(order.due_date) ||
        normalizeDateInput(order.return_date) ||
        todayString(),
      customer_id: order.customer_id,
      customer_name: order.customer_name || "",
      receiver_id: order.receiver_id,
      receiver_name: order.receiver_name || "",
      sale_order_id: order.sale_order_id,
      sale_order_code: order.sale_order_code || "",
      payment_method: order.payment_method || "",
      refund_now: Boolean(order.refund_now),
      no_refund: Boolean(order.no_refund),
      description: order.description || "",
      attachments: normalizedAttachments,
      items: sanitizedItems,
    });
    setExistingAttachments(normalizedAttachments);
    setAttachmentsInput(normalizedAttachments);
    setAttachmentsText(normalizedAttachments.join(", "));
    setItems(
      sanitizedItems.length
        ? ensureTempIds(sanitizedItems as ReturnOrderItemWithMeta[])
        : [
            {
              wms_id: undefined,
              category_id: undefined,
              product_id: undefined,
              product_name: "",
              quantity: 1,
              return_price: 0,
              return_amount: 0,
              __tempId: `temp-${itemIdRef.current++}`,
            },
          ],
    );
  }, [detailResponse, ensureTempIds]);

  useEffect(() => {
    if (isEdit) return;
    setForm(defaultFormValues);
    setItems([
      {
        wms_id: undefined,
        category_id: undefined,
        product_id: undefined,
        product_name: "",
        quantity: 1,
        return_price: 0,
        return_amount: 0,
        __tempId: `temp-${itemIdRef.current++}`,
      },
    ]);
    setAttachmentsInput([]);
    setAttachmentsText("");
    setExistingAttachments([]);
  }, [isEdit]);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      wmsCodeService.list(),
      wmsItemGroupService.list(),
      wmsItemService.list(),
      accountCodeService.list({ page: 1, pageSize: 500, type: "unit" }),
    ])
      .then(([codes, groups, itemsData, unitsRes]) => {
        if (!mounted) return;
        setWmsCodes(codes ?? []);
        setItemGroups(groups ?? []);
        setWmsItems(itemsData ?? []);
        const unitList =
          unitsRes?.data && Array.isArray(unitsRes.data)
            ? unitsRes.data
            : [];
        setUnits(unitList as any[]);
      })
      .catch(() => {
        if (!mounted) return;
        setWmsCodes([]);
        setItemGroups([]);
        setWmsItems([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!wmsItems.length) return;
    setItems((prev) =>
      prev.map((item) => {
        if (item.product_name || !item.product_id) return item;
        const selected = wmsItems.find(
          (product) => String(product.id) === String(item.product_id),
        );
        return selected?.name
          ? {
              ...item,
              product_name: selected.name,
              unit_id:
                selected?.unit_id ??
                (selected as any)?.unit?.id ??
                item.unit_id,
            }
          : item;
      }),
    );
  }, [wmsItems]);

  const saleOrderOptions = useMemo(
    () =>
      (saleOrdersData?.data as any[])?.map((so) => ({
        id: so.id,
        code: so.order_code ?? so.code ?? "",
      })) ?? [],
    [saleOrdersData],
  );

  const unitOptions = useMemo(
    () =>
      (units ?? []).map((u: any) => ({
        value: u.id,
        label: u.name ?? u.code ?? u.id,
      })),
    [units],
  );

  const totalReturnAmount = useMemo(
    () =>
      items.reduce((sum, item) => {
        const rawAmount =
          item.return_amount ??
          (item as any).convert_return_price ??
          (item as any).sale_amount ??
          0;
        return sum + normalizeNumber(rawAmount);
      }, 0),
    [items],
  );

  const handleFormChange = useCallback(
    (key: keyof ReturnOrderForm, value: any) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const handleAddItem = useCallback(() => {
    setItems((prev) => [
      ...prev,
      {
        wms_id: undefined,
        category_id: undefined,
        product_id: undefined,
        product_name: "",
        quantity: 1,
        return_price: 0,
        return_amount: 0,
        __tempId: `temp-${itemIdRef.current++}`,
      },
    ]);
  }, []);

  const handleItemChange = useCallback(
    (index: number, key: keyof ReturnOrderItem, value: any) => {
      setItems((prev) =>
        prev.map((item, idx) => {
          if (idx !== index) return item;
          const next: ReturnOrderItem = { ...item };

          if (key === "product_name") {
            next.product_name = value;
          } else if (key === "product_id") {
            const parsedProductId = normalizeId(value);
            next.product_id = parsedProductId;
            const selected = wmsItems.find(
              (product) => String(product.id) === String(parsedProductId),
            );
            const currentName = next.product_name
              ? String(next.product_name).trim()
              : "";
            if (selected?.name && !currentName) {
              next.product_name = selected.name;
            }
            if (
              selected?.unit_id !== undefined &&
              selected?.unit_id !== null
            ) {
              next.unit_id = Number(selected.unit_id);
            } else if ((selected as any)?.unit?.id) {
              next.unit_id = Number((selected as any).unit.id);
            }
          } else if (
            key === "wms_id" ||
            key === "category_id" ||
            key === "unit_id" ||
            key === "convert_unit_id"
          ) {
            next[key] = normalizeId(value);
          } else {
            const numericValue = normalizeNumber(value);
            next[key as any] =
              numericValue as ReturnOrderItem[keyof ReturnOrderItem];
          }

          const quantity = normalizeNumber(next.quantity);
          const price = normalizeNumber(next.return_price);
          const amount =
            key === "return_amount"
              ? normalizeNumber(value)
              : quantity * price || next.return_amount || 0;
          return {
            ...next,
            quantity,
            return_price: price,
            return_amount: amount,
          };
        }),
      );
    },
    [wmsItems],
  );

  const debouncedAttachmentsParse = useMemo(
    () =>
      debounce((text: string) => {
        const parsed = text
          .split(ATTACHMENT_SPLIT_REGEX)
          .map((v) => v.trim())
          .filter(Boolean);
        setAttachmentsInput(parsed);
      }, 250),
    [],
  );

  useEffect(() => {
    debouncedAttachmentsParse(attachmentsText);
  }, [attachmentsText, debouncedAttachmentsParse]);

  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const buildPayload = useCallback((): ReturnOrderForm | null => {
    if (!form.return_date || !form.sale_order_code) {
      SnackbarUtils.error("Ngày nhập lại và Mã đơn bán là bắt buộc");
      return null;
    }

    if (!form.customer_name || !form.receiver_name) {
      SnackbarUtils.error("Vui lòng nhập Khách hàng và Người nhận");
      return null;
    }

    const normalizedItems = sanitizeItems(items);

    if (!normalizedItems.length || !normalizedItems[0].quantity) {
      SnackbarUtils.error("Vui lòng nhập ít nhất một dòng hàng hoàn trả");
      return null;
    }

    return {
      ...form,
      attachments:
        attachmentsInput.length > 0
          ? normalizeAttachments(attachmentsInput)
          : existingAttachments,
      items: normalizedItems,
    };
  }, [attachmentsInput, existingAttachments, form, items]);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      const payload = buildPayload();
      if (!payload) return;

      if (isEdit && id) {
        updateReturnOrder.mutate(
          { id, ...payload },
          {
            onSuccess: () => {
              navigate(ROUTES.ACCOUNTING.PURCHASE_RETURN);
            },
          },
        );
        return;
      }

      createReturnOrder.mutate(payload, {
        onSuccess: () => {
          navigate(ROUTES.ACCOUNTING.PURCHASE_RETURN);
        },
      });
    },
    [buildPayload, createReturnOrder, id, isEdit, navigate, updateReturnOrder],
  );

  const isSaving = createReturnOrder.isPending || updateReturnOrder.isPending;

  return (
    <>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 uppercase">Chứng từ nhập lại</p>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEdit ? "Cập nhật chứng từ nhập lại" : "Thêm chứng từ nhập lại"}
            </h1>
            <p className="text-sm text-gray-600">
              Nhập thông tin chung và danh sách hàng hoàn trả.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(ROUTES.ACCOUNTING.PURCHASE_RETURN)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Danh sách
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
              disabled={isSaving}
            >
              <Save className="h-4 w-4" />
              {isSaving ? "Đang lưu..." : "Lưu chứng từ"}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="text-base font-semibold text-gray-900">
                Thông tin chung
              </h2>
            </div>
            <div className="grid gap-4 px-4 py-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Ngày nhập lại *
                </label>
                <input
                  type="date"
                  value={form.return_date}
                  onChange={(e) =>
                    handleFormChange("return_date", e.target.value)
                  }
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Hạn thanh toán *
                </label>
                <input
                  type="date"
                  value={form.due_date}
                  onChange={(e) => handleFormChange("due_date", e.target.value)}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Khách hàng *
                </label>
                <input
                  value={form.customer_name}
                  onChange={(e) =>
                    handleFormChange("customer_name", e.target.value)
                  }
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Tên khách hàng"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Người nhận *
                </label>
                <input
                  value={form.receiver_name}
                  onChange={(e) =>
                    handleFormChange("receiver_name", e.target.value)
                  }
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Người nhận hàng hoàn"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Mã đơn bán *
                </label>
                <select
                  value={form.sale_order_id ?? ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    const selected = saleOrderOptions.find(
                      (opt) => String(opt.id) === value,
                    );
                    setForm((prev) => ({
                      ...prev,
                      sale_order_id: value ? Number(value) : undefined,
                      sale_order_code: selected?.code ?? "",
                    }));
                  }}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  required
                >
                  <option value="">Chọn đơn bán</option>
                  {saleOrderOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.code || opt.id}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  PT thanh toán
                </label>
                <select
                  value={form.payment_method ?? ""}
                  onChange={(e) =>
                    handleFormChange("payment_method", e.target.value)
                  }
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                >
                  <option value="">Chọn phương thức</option>
                  <option value="cash">Tiền mặt</option>
                  <option value="bank">Ngân hàng</option>
                  <option value="debt">Công nợ</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={Boolean(form.refund_now)}
                    onChange={(e) =>
                      handleFormChange("refund_now", e.target.checked)
                    }
                    className="h-4 w-4"
                  />
                  Hoàn tiền ngay
                </label>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={Boolean(form.no_refund)}
                    onChange={(e) =>
                      handleFormChange("no_refund", e.target.checked)
                    }
                    className="h-4 w-4"
                  />
                  Không hoàn tiền
                </label>
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-gray-700">
                  Kèm theo (nhiều tệp)
                </label>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={attachmentsText}
                    onChange={(e) => {
                      setAttachmentsText(e.target.value);
                    }}
                    placeholder="tệp1.png, tệp2.pdf"
                    className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  />
                  {(attachmentsInput.length > 0 ||
                    existingAttachments.length > 0) && (
                    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <p className="text-xs font-semibold text-gray-600 mb-1">
                        Tệp đã chọn:
                      </p>
                      <ul className="space-y-1 text-xs text-gray-700 list-disc list-inside">
                        {(attachmentsInput.length > 0
                          ? attachmentsInput
                          : existingAttachments
                        ).map((item, idx) => (
                          <li key={`${idx}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-gray-700">
                  Mô tả
                </label>
                <textarea
                  value={form.description ?? ""}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                  rows={2}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Mô tả thêm"
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h2 className="text-base font-semibold text-gray-900">
                Hàng hoàn trả
              </h2>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-gray-500">Tổng tiền trả</p>
                  <p className="text-base font-semibold text-gray-900">
                    {totalReturnAmount.toLocaleString("vi-VN")} đ
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Plus className="h-4 w-4" />
                  Thêm dòng
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">STT</th>
                    <th className="px-3 py-2 text-left font-semibold">Kho</th>
                    <th className="px-3 py-2 text-left font-semibold">
                      Danh mục
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      Mã hàng
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      Tên hàng
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      Số lượng
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      Đơn vị
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      Đơn giá hoàn
                    </th>
                    <th className="px-3 py-2 text-right font-semibold">
                      Thành tiền
                    </th>
                    <th className="px-3 py-2 text-center font-semibold">
                      Thao tác
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {items.length ? (
                    items.map((item, index) => (
                      <tr key={`item-${index}`} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                        <td className="px-3 py-2 text-gray-900">
                          <select
                            value={item.wms_id ?? ""}
                            onChange={(e) =>
                              handleItemChange(index, "wms_id", e.target.value)
                            }
                            className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                          >
                            <option value="">Chọn kho</option>
                            {wmsCodes.map((code) => (
                              <option
                                key={`${code.id ?? code.code}`}
                                value={code.id}
                              >
                                {code.code}
                                {code.name ? ` - ${code.name}` : ""}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 text-gray-900">
                          <select
                            value={item.category_id ?? ""}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "category_id",
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                          >
                            <option value="">Chọn danh mục</option>
                            {itemGroups.map((group) => (
                              <option
                                key={`${group.id ?? group.code}`}
                                value={group.id}
                              >
                                {group.code}
                                {group.name ? ` - ${group.name}` : ""}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 text-gray-900">
                          <select
                            value={item.product_id ?? ""}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "product_id",
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                          >
                            <option value="">Chọn hàng</option>
                            {wmsItems.map((product) => (
                              <option
                                key={`${product.id ?? product.code}`}
                                value={product.id}
                              >
                                {product.code}
                                {product.name ? ` - ${product.name}` : ""}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 text-gray-900">
                          <input
                            value={item.product_name ?? ""}
                            onChange={(e) =>
                              handleItemChange(
                                index,
                                "product_name",
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                            placeholder="Tên hàng"
                          />
                        </td>
                        <td className="px-3 py-2 text-right text-gray-900">
                          <InputNumber
                            value={item.quantity ?? 0}
                            onValueChange={(values) =>
                              handleItemChange(
                                index,
                                "quantity",
                                values.floatValue ?? 0,
                              )
                            }
                            suffix=""
                            className="h-9 px-2 py-1 text-right text-sm"
                          />
                        </td>
                        <td className="px-3 py-2 text-gray-900">
                          <Select
                            className="text-sm w-36"
                            classNamePrefix="react-select"
                            placeholder="Chọn đơn vị"
                            isClearable
                            menuPortalTarget={
                              typeof document !== "undefined" ? document.body : undefined
                            }
                            menuPosition="fixed"
                            // styles={{
                            //   container: (base) => ({ ...base, width: "100%" }),
                            //   control: (base) => ({ ...base, minHeight: 32 }),
                            //   menuPortal: (base) => ({ ...base, zIndex: 40 }),
                            // }}
                            value={
                              unitOptions.find(
                                (opt) => String(opt.value) === String(item.unit_id),
                              ) || null
                            }
                            onChange={(opt) => {
                              const value = Array.isArray(opt)
                                ? opt[0]?.value
                                : opt?.value;
                              handleItemChange(index, "unit_id", value ?? "");
                            }}
                            options={unitOptions}
                            styles={{
                              control: (base) => ({
                                ...base,
                                minHeight: "36px",
                                borderColor: "#e5e7eb",
                                boxShadow: "none",
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                padding: "2px 8px",
                              }),
                              indicatorsContainer: (base) => ({
                                ...base,
                                paddingRight: "6px",
                              }),
                              menu: (base) => ({ ...base, zIndex: 10 }),
                            }}
                          />
                        </td>
                        <td className="px-3 py-2 text-right text-gray-900">
                          <InputNumber
                            value={item.return_price ?? 0}
                            onValueChange={(values) =>
                              handleItemChange(
                                index,
                                "return_price",
                                values.floatValue ?? 0,
                              )
                            }
                            className="h-9 px-2 py-1 text-right text-sm"
                            suffix=" ₫"
                          />
                        </td>
                        <td className="px-3 py-2 text-right text-gray-900">
                          <InputNumber
                            value={item.return_amount ?? 0}
                            onValueChange={(values) =>
                              handleItemChange(
                                index,
                                "return_amount",
                                values.floatValue ?? 0,
                              )
                            }
                            className="h-9 px-2 py-1 text-right text-sm"
                            suffix=" ₫"
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700"
                            disabled={items.length === 1}
                          >
                            <Trash2 className="h-4 w-4" />
                            Xóa
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        Chưa có dòng chi tiết. Thêm mới để bắt đầu.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <p className="text-sm text-gray-500">
                Tổng cộng:{" "}
                <span className="font-semibold text-gray-900">
                  {totalReturnAmount.toLocaleString("vi-VN")} đ
                </span>
              </p>
              <button
                type="button"
                onClick={handleSubmit}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
                disabled={isSaving}
              >
                <Save className="h-4 w-4" />
                {isSaving ? "Đang lưu..." : "Lưu chứng từ"}
              </button>
            </div>
          </div>
        </form>
      </div>
      {(isFetchingDetail || isSaving) && (
        <LoadingState
          message={isEdit ? "Đang tải chứng từ" : "Đang lưu chứng từ"}
          description="Vui lòng chờ trong giây lát."
        />
      )}
    </>
  );
};

export default ReturnOrderDetail;
